import { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Action } from 'redux';

import lang from '../../lib/language';
import { PlanPhaseTypeEnum } from '../../lib/models/crm/types';
import { RootState } from '../../lib/store';
import { getCurrentAccountTradingPlan } from '../../lib/store/crm/helpers';
import { getPayments } from '../../lib/store/payment';
import { ALL_TRANSACTIONS_FILTER } from '../../lib/store/payment/constants';
import { isUserVerifiedStatus } from '../../lib/store/selectors';

import {
  BaseModal,
  Grid,
  MobileDrawer,
  NotAvailableWithdrawals,
  PayoutsHeader,
  PayoutsRules,
} from '../../components';
import { useBrowserWidth, useIsMobile } from '../../hooks';
import Button from '../../shared_components/Button/Button';
import Transactions from '../Funds/transactions/Transactions';

import styles from './Payouts.module.scss';

const Payouts = ({ currentPlan, fetchPayments }: ReduxProps) => {
  const { isMobile, isTablet } = useIsMobile();
  const { browserWidthSize } = useBrowserWidth();
  const individual = useSelector((state: RootState) => state.crm.individualExtendedInfo);
  const isUserVerified = isUserVerifiedStatus(individual);
  const planPhase = currentPlan?.phase;
  const buyingPower = currentPlan?.daily_hard_bp;
  const isChallengePhase = planPhase === PlanPhaseTypeEnum.challenge;
  const location = useLocation();
  const locationState = location.state;
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(
    locationState?.showWithdrawalModal,
  );
  const [showCryptoWithdrawalModal, setShowCryptoWithdrawalModal] = useState(
    locationState?.showCryptoWithdrawalModal,
  );
  const cryptoWithdrawalAmount = localStorage.getItem('cryptoWithdrawalAmount') ?? '0';
  const withdrawMessages = lang.mobileWithdrawScreenModal().split('\n\n');

  useEffect(() => {
    if (!showWithdrawalModal && !showCryptoWithdrawalModal && isUserVerified) {
      fetchPayments();
    }
  }, [showWithdrawalModal, showCryptoWithdrawalModal]);

  const closeWithdrawalModal = () => {
    setShowWithdrawalModal(false);
  };

  const closeCryptoWithdrawalModal = () => {
    localStorage.removeItem('cryptoWithdrawalAmount');
    setShowCryptoWithdrawalModal(false);
  };

  const cryptoWithdrawalModalBody = () => (
    <>
      <div className={styles.textWrapper}>
        <div className={styles.description}>
          {lang.cryptoWithdrawModalDescription(`$${cryptoWithdrawalAmount}`)}
        </div>
      </div>
      <Button
        fullWidth
        biggerFontSize
        className={styles.okBtn}
        onClick={closeCryptoWithdrawalModal}
      >
        {lang.commonOkay()}
      </Button>
    </>
  );

  const withdrawalModalBody = () => (
    <>
      <div className={styles.textWrapper}>
        <div className={styles.description}>{withdrawMessages[0]}</div>
        <div className={styles.description}>{withdrawMessages[1]}</div>
      </div>
      <Button
        fullWidth
        biggerFontSize
        className={styles.okBtn}
        onClick={() => setShowWithdrawalModal(false)}
      >
        {lang.commonOkay()}
      </Button>
    </>
  );

  const gridMainContent = (
    <div className={styles.container}>
      <div className={styles.firstContainerContent}>
        <PayoutsHeader isUserVerified={isUserVerified} currentPlan={currentPlan} />
        {(isMobile || isTablet) && <PayoutsRules buyingPower={buyingPower} />}
        <Transactions isUserVerified={isUserVerified} browserWidthSize={browserWidthSize} />
      </div>
    </div>
  );

  const gridExtraContent = !isMobile && !isTablet ? <PayoutsRules buyingPower={buyingPower} /> : null;

  return (
    <>
      <Grid
        extraChildren={gridExtraContent}
        notificationArea={
          isChallengePhase && (
            <div className={styles.container}>
              <NotAvailableWithdrawals />
            </div>
          )
        }
      >
        {gridMainContent}
      </Grid>

      {!isMobile ? (
        <BaseModal isOpen={showWithdrawalModal} toggle={closeWithdrawalModal}>
          <div className={styles.title}>{lang.mobileWithdrawScreenModalTitle()}</div>
          {withdrawalModalBody()}
        </BaseModal>
      ) : (
        <MobileDrawer
          title={lang.mobileWithdrawScreenModalTitle()}
          isOpen={showWithdrawalModal}
          onClose={closeWithdrawalModal}
        >
          {withdrawalModalBody()}
        </MobileDrawer>
      )}

      {!isMobile ? (
        <BaseModal isOpen={showCryptoWithdrawalModal} toggle={closeCryptoWithdrawalModal}>
          <div className={styles.title}>{lang.cryptoWithdrawModalTitle()}</div>
          {cryptoWithdrawalModalBody()}
        </BaseModal>
      ) : (
        <MobileDrawer
          title={lang.cryptoWithdrawModalTitle()}
          isOpen={showCryptoWithdrawalModal}
          onClose={closeCryptoWithdrawalModal}
        >
          {cryptoWithdrawalModalBody()}
        </MobileDrawer>
      )}
    </>
  );
};
const mapStateToProps = (state: RootState) => ({
  currentPlan: getCurrentAccountTradingPlan(state.crm),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  fetchPayments: () => dispatch(getPayments(ALL_TRANSACTIONS_FILTER)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Payouts);
